import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'

const VIEW_W = 800
const VIEW_H = 280
const PAD = { top: 24, right: 20, bottom: 40, left: 72 }
const TICKS = 4

function niceStep(range, tickCount) {
  if (!isFinite(range) || range <= 0) return 1
  const roughStep = range / tickCount
  const exp = Math.floor(Math.log10(roughStep))
  const fraction = roughStep / Math.pow(10, exp)
  let niceFraction
  if (fraction <= 1) niceFraction = 1
  else if (fraction <= 2) niceFraction = 2
  else if (fraction <= 5) niceFraction = 5
  else niceFraction = 10
  return niceFraction * Math.pow(10, exp)
}

export function formatCompact(value) {
  const n = Number(value) || 0
  if (n === 0) return '0'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) {
    const v = n / 1_000_000
    return (abs >= 10_000_000 ? Math.round(v) : v.toFixed(1).replace(/\.0$/, '')) + 'M'
  }
  if (abs >= 1_000) {
    const v = n / 1_000
    return (abs >= 10_000 ? Math.round(v) : v.toFixed(1).replace(/\.0$/, '')) + 'K'
  }
  return n.toLocaleString('es-CO')
}

function LineChartSvg({
  title,
  labels = [],
  values = [],
  color = '#2E7DD7',
  valueFormatter = (v) => Number(v).toLocaleString('es-CO'),
  yTickFormatter,
  integerOnly = false,
}) {
  const [hover, setHover] = useState(null)

  if (!values || values.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', color: '#888' }}>
        {title && <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#333' }}>{title}</Typography>}
        Sin datos para graficar
      </Box>
    )
  }

  const maxValue = Math.max(...values.map((v) => Number(v) || 0), 0)
  let step = niceStep(maxValue || 1, TICKS)
  if (integerOnly) step = Math.max(1, Math.ceil(step))
  const niceMax = step * TICKS

  const ticks = []
  for (let i = 0; i <= TICKS; i++) ticks.push(step * i)

  const tickFormatter = yTickFormatter || ((t) => (integerOnly ? Math.round(t).toString() : valueFormatter(t)))

  const innerW = VIEW_W - PAD.left - PAD.right
  const innerH = VIEW_H - PAD.top - PAD.bottom
  const xStep = values.length > 1 ? innerW / (values.length - 1) : 0

  const points = values.map((v, i) => {
    const num = Number(v) || 0
    return {
      x: PAD.left + (values.length > 1 ? i * xStep : innerW / 2),
      y: PAD.top + innerH - (num / niceMax) * innerH,
      value: num,
      label: labels[i] || '',
    }
  })

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')
  const lastX = points[points.length - 1].x
  const firstX = points[0].x
  const baseY = PAD.top + innerH
  const areaD = `${pathD} L${lastX},${baseY} L${firstX},${baseY} Z`

  const skipFactor = Math.max(1, Math.ceil(points.length / 10))
  const gradId = `chartGrad-${color.replace('#', '')}`

  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#333' }}>
          {title}
        </Typography>
      )}
      <Box sx={{ position: 'relative', width: '100%' }}>
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          width="100%"
          style={{ display: 'block' }}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.25} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>

          {ticks.map((t, i) => {
            const y = PAD.top + innerH * (1 - i / TICKS)
            return (
              <g key={`tick-${i}`}>
                <line
                  x1={PAD.left}
                  x2={VIEW_W - PAD.right}
                  y1={y}
                  y2={y}
                  stroke="#eaeaea"
                  strokeWidth={1}
                />
                <text
                  x={PAD.left - 8}
                  y={y + 4}
                  fontSize={11}
                  textAnchor="end"
                  fill="#888"
                >
                  {tickFormatter(t)}
                </text>
              </g>
            )
          })}

          <line
            x1={PAD.left}
            x2={VIEW_W - PAD.right}
            y1={baseY}
            y2={baseY}
            stroke="#bbb"
            strokeWidth={1}
          />

          {points.map((p, i) => {
            if (i % skipFactor !== 0 && i !== points.length - 1) return null
            return (
              <text
                key={`xlabel-${i}`}
                x={p.x}
                y={baseY + 18}
                fontSize={11}
                textAnchor="middle"
                fill="#888"
              >
                {p.label}
              </text>
            )
          })}

          <path d={areaD} fill={`url(#${gradId})`} />
          <path
            d={pathD}
            fill="none"
            stroke={color}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {points.map((p, i) => (
            <g key={`pt-${i}`}>
              <circle
                cx={p.x}
                cy={p.y}
                r={hover === i ? 5 : 3.5}
                fill={hover === i ? color : '#fff'}
                stroke={color}
                strokeWidth={2}
              />
              <circle
                cx={p.x}
                cy={p.y}
                r={14}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
                style={{ cursor: 'pointer' }}
              />
            </g>
          ))}
        </svg>

        {hover !== null && (
          <Box
            sx={{
              position: 'absolute',
              left: `${(points[hover].x / VIEW_W) * 100}%`,
              top: `${(points[hover].y / VIEW_H) * 100}%`,
              transform: 'translate(-50%, calc(-100% - 10px))',
              background: 'rgba(15, 23, 42, 0.92)',
              color: '#fff',
              fontSize: '0.78rem',
              padding: '6px 10px',
              borderRadius: '8px',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
              zIndex: 2,
            }}
          >
            <div style={{ fontWeight: 700, opacity: 0.85, fontSize: '0.7rem' }}>
              {points[hover].label}
            </div>
            <div style={{ fontWeight: 700 }}>{valueFormatter(points[hover].value)}</div>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default LineChartSvg
